/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useRef, useState } from 'react';

import { cssMap, cx, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { PromptInputContext } from '@atlassian/conversation-assistant-external-context/types';
import { PreviewCard } from '@atlassian/conversation-assistant-ui-components/ui/PreviewCard';

import { SearchInactiveContent } from '../../../ui/chat-prompt-input/SearchInactiveContent';
import { useSearchMode } from '../../../ui/chat-prompt-input/useSearchMode';
import { type UploadedFile } from '../../controllers/attachtment-store/UploadedFile';
import { useAllAttachmentsStore } from '../../controllers/attachtment-store/useAllAttachmentsStore';
import { FileCard } from './file-card';

const styles = cssMap({
	listWrapper: {
		display: 'flex',
		maxWidth: '100%',
		gap: token('space.150'),
		listStyle: 'none',
		margin: 0,
		paddingBlockStart: token('space.200'),
		paddingBlockEnd: token('space.150'),
	},
	scrollingList: {
		overflowX: 'auto',
		whiteSpace: 'nowrap',
		scrollBehavior: 'smooth',
	},
	standardScrollingList: {
		paddingInlineStart: token('space.150'),
		paddingInlineEnd: token('space.150'),
	},
	fullWidthScrollingList: {
		paddingInlineStart: 0,
		paddingInlineEnd: 0,
	},
	listItem: {
		marginBlockStart: 0,
		listStyle: 'none',
	},
});

const useAutoScrollOnDataChange = <T,>(data: T[]) => {
	const prevDataLength = useRef(data.length);
	const [isAutoScroll, setIsAutoScroll] = useState(false);

	const dataLength = data.length;

	useEffect(() => {
		if (prevDataLength.current < dataLength) {
			setIsAutoScroll(true);
		} else {
			setIsAutoScroll(false);
		}
		prevDataLength.current = dataLength;
	}, [dataLength]);

	return isAutoScroll;
};

export const InputContextListDisplay = ({
	attachtmentsStoreKey,
	layout = 'scroll',
}: {
	attachtmentsStoreKey: string;
	layout?: 'scroll' | 'full-width-scroll';
}): JSX.Element | null => {
	const searchMode = useSearchMode();
	const [attachmentsState, { removeExternalContext, removeFile }] =
		useAllAttachmentsStore(attachtmentsStoreKey);
	const isAutoScroll = useAutoScrollOnDataChange(attachmentsState);

	if (attachmentsState.length === 0) {
		return null;
	}

	return (
		<Box
			as="ul"
			xcss={cx(
				styles.listWrapper,
				styles.scrollingList,
				layout === 'scroll' && styles.standardScrollingList,
				layout === 'full-width-scroll' && styles.fullWidthScrollingList,
			)}
		>
			{attachmentsState.map((context, index) => {
				const isLast = index === attachmentsState.length - 1;
				let key: string;
				let content: JSX.Element;

				switch (context.type) {
					case 'file':
						const file = context.data as UploadedFile;
						key = file.name;
						content = (
							<SearchInactiveContent as="div">
								<FileCard
									isDisabled={Boolean(searchMode)}
									fileName={file.name}
									onRemove={() => removeFile(attachtmentsStoreKey, file.name)}
									isLoading={file.isLoading}
									error={file.error}
									autoScroll={isAutoScroll && Boolean(file.error || isLast)}
									previewBlobUrl={file.previewBlobUrl}
									isPreviewLoading={file.isPreviewLoading}
									fileObject={file.fileObject}
								/>
							</SearchInactiveContent>
						);
						break;
					case 'external':
						const promptInputContext = context.data as PromptInputContext;
						const externalContext = promptInputContext.representation;
						key = promptInputContext.key;
						content = (
							<PreviewCard
								label={externalContext.label}
								subLabel={externalContext.description}
								onRemove={() => removeExternalContext(attachtmentsStoreKey, promptInputContext.key)}
								autoScroll={isAutoScroll && Boolean(isLast)}
								cardIcon={externalContext.image}
							/>
						);
						break;
					default:
						return null;
				}

				return (
					<Box as="li" key={key} xcss={styles.listItem}>
						{content}
					</Box>
				);
			})}
		</Box>
	);
};
